import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRightLeft, ExternalLink } from "lucide-react";
import { formatDate } from "@/lib/date-utils";
import type { EquipmentConflict } from "@/hooks/useEquipmentConflicts";
import { cn } from "@/lib/utils";

interface AvailableInstance {
  id: string;
  identifier: string;
}

interface EquipmentConflictDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: string;
  equipmentName: string;
  isSerialized: boolean;
  stockQuantity: number | null;
  activeInstanceCount: number | null;
  conflicts: EquipmentConflict[];
  currentProjectId: string;
  overlappingInstanceIds: string[];
  availableInstances?: AvailableInstance[];
  onReplaceInstance?: (oldInstanceId: string, newInstanceId: string) => void;
}

export function EquipmentConflictDialog({
  open,
  onClose,
  equipmentId,
  equipmentName,
  isSerialized,
  stockQuantity,
  activeInstanceCount,
  conflicts,
  currentProjectId,
  overlappingInstanceIds,
  availableInstances = [],
  onReplaceInstance,
}: EquipmentConflictDialogProps) {
  const [replacingInstanceId, setReplacingInstanceId] = useState<string | null>(
    null,
  );

  const totalUsed = conflicts.reduce((sum, c) => sum + c.amount, 0);
  const availableQuantity =
    activeInstanceCount !== null ? activeInstanceCount : stockQuantity;
  const quantityLabel =
    activeInstanceCount !== null ? "Active instances" : "Stock";

  // Check if any conflict has instances assigned
  const hasAnyInstances = conflicts.some((c) => c.instances.length > 0);

  const overlappingSet = new Set(overlappingInstanceIds);

  // Instances that are free (not used in any overlapping project in this timeframe)
  const usedInstanceIds = new Set(
    conflicts.flatMap((c) => c.instances.map((i) => i.instanceId)),
  );
  const freeInstances = availableInstances.filter(
    (i) => !usedInstanceIds.has(i.id) && i.id !== replacingInstanceId,
  );

  const handleReplace = (oldInstanceId: string, newInstanceId: string) => {
    onReplaceInstance?.(oldInstanceId, newInstanceId);
    setReplacingInstanceId(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setReplacingInstanceId(null);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {equipmentName}
            <Button variant="ghost" size="icon" className="h-6 w-6" asChild>
              <a
                href={`/equipment/${equipmentId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-muted-foreground mb-4">
          {quantityLabel}: {availableQuantity ?? "Not set"} | Used in timeframe:{" "}
          {totalUsed}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Dates</TableHead>
              {isSerialized && hasAnyInstances && (
                <TableHead>Instances</TableHead>
              )}
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {conflicts.map((conflict, index) => {
              const isCurrentProject = conflict.projectId === currentProjectId;

              return (
                <TableRow
                  key={`${conflict.projectId}-${index}`}
                  className={cn(isCurrentProject && "bg-muted/50")}
                >
                  <TableCell className="font-medium">
                    <div>
                      {isCurrentProject ? (
                        <>
                          <span className="mr-1">→</span>
                          {conflict.projectName}
                        </>
                      ) : (
                        <a
                          href={`/projects/${conflict.projectId}/equipment`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          {conflict.projectName}
                        </a>
                      )}
                      {conflict.groupName && (
                        <div className="text-xs text-muted-foreground font-normal">
                          {conflict.groupName}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {formatDate(conflict.projectStart)} -{" "}
                    {formatDate(conflict.projectEnd)}
                  </TableCell>
                  {isSerialized && hasAnyInstances && (
                    <TableCell>
                      {conflict.instances.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {conflict.instances.map((inst) => {
                            const isConflicting = overlappingSet.has(
                              inst.instanceId,
                            );
                            const canReplace =
                              isCurrentProject &&
                              isConflicting &&
                              onReplaceInstance &&
                              freeInstances.length > 0;

                            return (
                              <span
                                key={inst.instanceId}
                                className="inline-flex items-center gap-0.5"
                              >
                                {replacingInstanceId === inst.instanceId ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Badge
                                      variant="destructive"
                                      className="text-xs line-through"
                                    >
                                      {inst.identifier}
                                    </Badge>
                                    <Select
                                      onValueChange={(newId) =>
                                        handleReplace(inst.instanceId, newId)
                                      }
                                    >
                                      <SelectTrigger className="h-6 w-28 text-xs">
                                        <SelectValue placeholder="Replace..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {freeInstances.map((fi) => (
                                          <SelectItem key={fi.id} value={fi.id}>
                                            {fi.identifier}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() =>
                                        setReplacingInstanceId(null)
                                      }
                                    >
                                      ×
                                    </Button>
                                  </span>
                                ) : (
                                  <>
                                    <Badge
                                      variant={
                                        isConflicting
                                          ? "destructive"
                                          : "outline"
                                      }
                                      className="text-xs"
                                    >
                                      {inst.identifier}
                                    </Badge>
                                    {canReplace && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5"
                                        title="Replace with available instance"
                                        onClick={() =>
                                          setReplacingInstanceId(
                                            inst.instanceId,
                                          )
                                        }
                                      >
                                        <ArrowRightLeft className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">
                          None assigned
                        </span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    {conflict.amount}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow className="font-semibold border-t-2">
              <TableCell>Total</TableCell>
              <TableCell></TableCell>
              {isSerialized && hasAnyInstances && <TableCell></TableCell>}
              <TableCell className="text-right">{totalUsed}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}
